import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import { HELP_CENTER_URL } from '@highlight-cp/constants';
import { Button, Center, Code, Group, Stack, Text, Title } from '@highlight-cp/core-ui';
import { ExternalLink } from '@highlight-cp/core-ui-compositions';

interface SignedInNotFoundProps {
  /** The path that wasn't found, without its query or hash. */
  missingPath: string;
}

/**
 * The 404 body for a signed-in viewer. Router-agnostic, so both the Pages Router 404 and the App
 * Router's `not-found.tsx` render it without pulling in the other router's modules.
 */
const SignedInNotFound = ({ missingPath }: SignedInNotFoundProps) => {
  const { t } = useTranslation();

  return (
    <Center mih="60vh" px="md">
      <Stack align="center" gap="lg" maw={480}>
        <Stack align="center" gap="xs">
          <Title order={2} ta="center">
            {t("We can't find that page")}
          </Title>
          <Text c="dimmed" ta="center">
            {t('The link may be broken, or the page may have moved or been removed.')}
          </Text>
        </Stack>
        {/* Block so a long path scrolls inside the chip, not the page. */}
        <Code block color="var(--mantine-color-body)" maw="100%">
          {missingPath}
        </Code>
        <Group justify="center">
          <Button component={Link} href="/home">
            {t('Go to home')}
          </Button>
          <Button component={Link} href="/project" variant="default">
            {t('View projects')}
          </Button>
        </Group>
        <Text size="sm" c="dimmed" ta="center">
          {t('Think something is missing?')}{' '}
          <ExternalLink href={HELP_CENTER_URL}>{t('Visit our help center')}</ExternalLink>
        </Text>
      </Stack>
    </Center>
  );
};

export default SignedInNotFound;
